import { useQuery } from '@tanstack/react-query'
import { Check, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { savingsAccountListQueryOptions } from '@/api/savings-api'
import type { SavingsAccount } from '@/api/savings-types'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

import { productClassLabel } from './constants'

const SEARCH_DEBOUNCE_MS = 300

/**
 * Choosing the other side of a transfer.
 *
 * The endpoint wants the destination account's identifier, and an identifier is not a thing anybody
 * has at a counter — the customer has an account number, or a passbook, or a name. Asking a teller
 * to type an opaque identifier would mean copying it from somewhere, and a copied identifier that
 * is one character wrong is money in a stranger's account with a valid-looking receipt.
 *
 * So the destination is searched for and then *confirmed by its holder's name*. The account list
 * endpoint already searches over account number, customer number and name, and it is scoped to the
 * caller's branch reach — which is the same reach the transfer itself is checked against, so an
 * account that cannot be found here is one the transfer would have refused anyway with
 * `savings.transfer_branch_mismatch`.
 *
 * Nothing is auto-selected, not even a single exact match. The confirmation that the name on screen
 * is the person the money is meant for is the entire safeguard, and a screen that picks for the
 * operator removes it.
 */
export function DestinationAccountPicker({
  sourceAccountId,
  currency,
  selected,
  onSelect,
}: {
  /** Excluded from the results: the server refuses a transfer to the same account. */
  sourceAccountId: string
  currency: string
  selected: SavingsAccount | null
  onSelect: (account: SavingsAccount | null) => void
}) {
  const [text, setText] = useState('')
  const [term, setTerm] = useState('')

  useEffect(() => {
    const trimmed = text.trim()
    if (trimmed === term) {
      return
    }

    const timer = window.setTimeout(() => {
      setTerm(trimmed)
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [text, term])

  const query = useQuery({
    ...savingsAccountListQueryOptions({ search: term, status: 'Active', page: 1, pageSize: 10 }),
    // Two characters is short enough for a partial account number and long enough that opening the
    // form does not fire a search for every account at the branch.
    enabled: term.length >= 2,
  })

  const candidates = (query.data?.items ?? []).filter(
    (account) => account.id !== sourceAccountId,
  )

  if (selected) {
    return (
      <div className="space-y-1.5">
        <Label>Destination account</Label>
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-brand-fill/40 bg-brand-fill-subtle p-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Check className="size-4 text-brand-text" aria-hidden />
              <span className="font-medium text-heading">{selected.customerName}</span>
              <StatusBadge status={selected.status} />
            </div>
            <p className="font-mono text-xs text-body">
              {selected.accountNumber} · {selected.customerNumber}
            </p>
            <p className="text-xs text-muted">
              {productClassLabel(selected.productClass)} · balance{' '}
              <MoneyDisplay
                value={selected.ledgerBalanceMinor}
                currency={selected.currencyCode}
                className="inline text-left"
              />
            </p>
            {selected.currencyCode !== currency ? (
              <p className="text-xs text-danger-text">
                This account is held in {selected.currencyCode} and the source is in {currency}. The
                server refuses that with savings.currency_mismatch.
              </p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onSelect(null)
              setText('')
              setTerm('')
            }}
          >
            <X />
            Change
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor="transfer-destination-search">Destination account</Label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
          aria-hidden
        />
        <Input
          id="transfer-destination-search"
          value={text}
          autoComplete="off"
          spellCheck={false}
          placeholder="Account number, customer number, or name"
          className="pl-9"
          onChange={(event) => setText(event.target.value)}
          aria-describedby="transfer-destination-help"
        />
      </div>
      <p id="transfer-destination-help" className="text-xs text-muted">
        Search, then confirm the holder&apos;s name before posting. Only accounts at a branch you can
        act for are found — and only those are accounts the transfer would be permitted to reach.
      </p>

      {term.length < 2 ? null : query.isPending ? (
        <div className="space-y-1.5">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : query.isError ? (
        <p className="text-xs text-danger-text">
          The account search failed. Nothing has been selected — do not post until a destination is
          confirmed.
        </p>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-muted">
          No active account at your branches matches “{term}”. A closed, dormant, or frozen account
          will not appear here, and could not receive the money in any case.
        </p>
      ) : (
        <ul className="divide-y divide-border-subtle overflow-hidden rounded-card border border-border-subtle">
          {candidates.map((account) => (
            <li key={account.id}>
              <button
                type="button"
                onClick={() => onSelect(account)}
                className="flex w-full flex-wrap items-center justify-between gap-2 p-3 text-left transition-colors hover:bg-surface-hover"
              >
                <div className="min-w-0 space-y-0.5">
                  <span className="block text-sm font-medium text-heading">
                    {account.customerName}
                  </span>
                  <span className="block font-mono text-2xs text-muted">
                    {account.accountNumber} · {account.customerNumber}
                  </span>
                </div>
                <span className="text-2xs text-muted">
                  {productClassLabel(account.productClass)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
